import { environment as ENV } from '@environment';
import { DatePipe } from '@angular/common';

import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { RappiTravelModules } from '@ux/entities/modules';
import { Countries } from '@ux/entities/country.enum';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { DATE_INVALID } from '@app/constants/date.constants';
import { isNullOrUndefined } from '@app/util/util';

const TIME_ZONE_CONST = 'T';

export interface RestrictDateValidator {
  open_search: boolean;
  international_dates: {
    minDate: string,
    maxDate: string
  };
  national_dates: {
    minDate: string,
    maxDate: string
  };
}

export interface RestrictExceptDestination {
  code: string;
  validate_dates: boolean;
}

export function validateformatDate(date: Date): Date | null {
  return date.toString() !== DATE_INVALID ? date : null;
}

export function compareToCurrentDateWithoutTime(date: Date): boolean {
  return date ? new Date(date?.setHours(0, 0, 0, 0)) < new Date(new Date()?.setHours(0, 0, 0, 0)) : false;
}

export function formatDateByRegex(date: string): Date | null {
  const dateFormat = new Date(date?.replace(new RegExp(/-/g), '/'));
  return validateformatDate(dateFormat) ? dateFormat : null;
}

export function formatStringDateByRegex(date: string): string {
  return date?.replace(new RegExp(/-/g), '/');
}

export function arrivalDaysFormatDateByRegex(date: string): Date {
  const indexTimeZone = date.search(TIME_ZONE_CONST);
  const dateSection = indexTimeZone > 0 ? date.substring(0, indexTimeZone) : date;
  return formatDateByRegex(dateSection);
}

export function isNationalSearch(place: AutocompletePlace): boolean {
  return place?.country?.toUpperCase() === ENV.countryInfo.name.toUpperCase();
}

export function canSearch(module: RappiTravelModules): boolean {
  return ENV.validate_search[module].open_search;
}

export function blockSearchByDates(place: AutocompletePlace, date: Date,
  validator: RestrictDateValidator = ENV.validate_search.flighs): boolean {
  if (isNationalSearch(place)) {
    return date >= new Date(validator.national_dates.minDate) && date < new Date(validator.national_dates.maxDate);
  } else {
    return date >= new Date(validator.international_dates.minDate) && date < new Date(validator.international_dates.maxDate);
  }
}

export function untilDateByCountry(arrivalPlace: AutocompletePlace, departurePlace: AutocompletePlace,
  validator: RestrictDateValidator = ENV.validate_search.flighs): RestrictDateValidator {
  return validator[Countries[arrivalPlace.country]] || validator[Countries[departurePlace.country]] || validator;
}

export function blockSearchByCountry(arrivalPlace: AutocompletePlace, departurePlace: AutocompletePlace,
  date: Date): boolean {
  const untilDate = untilDateByCountry(arrivalPlace, departurePlace);
  if (untilDate) {
    return date < new Date(untilDate.international_dates.maxDate);
  }
}

export function formatDate(date: Date, datePipe?: DatePipe, translateService?: TranslatePipe): string {
  return `${datePipe.transform(date, 'd')}
    ${translateService.transform('BLOCK_SEARCH_MESSAGE.OF')} ${datePipe.transform(date, 'MMMM y')}`;
}

export function formatStrinWithTimeZoneDateByRegex(date: string): string {
  if (date) {
    const parts = date.split(TIME_ZONE_CONST);
    if (parts.length) {
      return parts[0].replace(new RegExp(/-/g), '/');
    } else {
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0);
      const day = newDate.getDate();
      const month = newDate.getMonth() + 1;
      const year = newDate.getUTCFullYear();
      const mo = month < 10 ? `0${month}` : month;
      const da = day < 10 ? `0${day}` : day;
      return `${year}/${mo}/${da}`;
    }
  } else return '';
}

export function toDateUtc(dateString: string): Date {
  const sp = dateString?.match(/\d+/g) || [];
  if (sp?.length >= 6) {
    return new Date(+sp[0], +sp[1] - 1, +sp[2], +sp[3], +sp[4], +sp[5]);
  } else if (sp?.length >= 3) {
    return new Date(+sp[0], +sp[1] - 1, +sp[2], 0, 0, 0);
  } else {
    return null;
  }
}

export function dobToDate(dobTz: string): Date {
  const dobParts = dobTz?.split(TIME_ZONE_CONST);
  const dob = dobParts?.[0];
  if (!isNullOrUndefined(dob?.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/))) {
    const split = dob?.split('/');
    return new Date(+split?.[2], +split?.[1] - 1, +split?.[0]);
  } else if (!isNullOrUndefined(dob?.match(/^\d{4}\/\d{1,2}\/\d{1,2}$/))) {
    const split = dob?.split('/');
    return new Date(+split?.[0], +split?.[1] - 1, +split?.[2]);
  } else if (dob?.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(formatStringDateByRegex(dob));
  } else {
    return null;
  }
}

export function extractHour(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}
